/**
 * oddit-section-main-product.js
 *  - gallery: mobile thumbs swap the main image, desktop thumbs open the lightbox
 *  - variant picker: resolves the variant from all selected options and updates price,
 *    add-to-cart button, main image, ?variant= URL and the sticky add-to-cart bar
 *  - sticky add-to-cart bar: shown once the in-page buy buttons scroll out of view
 *  - add to cart (main + sticky + bundle) through window.OdditCartDrawer, then opens the drawer
 *  - bundle card: live [count] / [total] labels
 */
(function () {
  'use strict';

  var desktop = window.matchMedia('(min-width: 990px)');
  var ACTIVE_THUMB = 'oddit-section-main-product__thumbnail--active';
  var ACTIVE_SLIDE = 'oddit-section-main-product__slide--active';

  function formatMoney(cents, format) {
    var value = cents / 100;
    var noDecimals = format.indexOf('no_decimals') !== -1;
    var comma = format.indexOf('comma_separator') !== -1;
    var amount = value.toFixed(noDecimals ? 0 : 2).replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
    if (comma && !noDecimals) amount = amount.replace(/\.(\d{2})$/, ',$1');
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* ---------------------------------------------------------------- Gallery */

  // Returns showMedia(mediaId) so the variant picker can swap the main image
  function initGallery(section, openLightbox) {
    var thumbnails = section.querySelectorAll('[data-thumbnail]');
    var track = section.querySelector('[data-gallery-track]');
    var slides = track ? Array.prototype.slice.call(track.querySelectorAll('[data-slide]')) : [];
    if (!track || !slides.length) return function () {};
    var scrollTicking = false;

    function slideFor(mediaId) {
      return track.querySelector('[data-slide][data-media-id="' + mediaId + '"]');
    }

    function setActive(mediaId) {
      var id = String(mediaId);
      slides.forEach(function (slide) {
        slide.classList.toggle(ACTIVE_SLIDE, slide.getAttribute('data-media-id') === id);
      });
      thumbnails.forEach(function (thumb) {
        var on = thumb.getAttribute('data-media-id') === id;
        thumb.classList.toggle(ACTIVE_THUMB, on);
        thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function scrollToSlide(slide, smooth) {
      if (desktop.matches) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    }

    function nearestSlide() {
      var x = track.scrollLeft;
      var best = null;
      var bestDistance = Infinity;
      slides.forEach(function (slide) {
        var distance = Math.abs(slide.offsetLeft - track.offsetLeft - x);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = slide;
        }
      });
      return best;
    }

    track.addEventListener('scroll', function () {
      if (desktop.matches || scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(function () {
        scrollTicking = false;
        var slide = nearestSlide();
        if (slide && !slide.classList.contains(ACTIVE_SLIDE)) setActive(slide.getAttribute('data-media-id'));
      });
    }, { passive: true });

    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        // Desktop thumbs sit in a grid far below the main image, so open the viewer instead of swapping
        if (desktop.matches && openLightbox) {
          openLightbox(Number(thumb.getAttribute('data-media-index')));
          return;
        }
        var id = thumb.getAttribute('data-media-id');
        setActive(id);
        var slide = slideFor(id);
        if (slide) scrollToSlide(slide, true);
      });
    });

    slides.forEach(function (slide) {
      slide.addEventListener('click', function () {
        if (desktop.matches || !openLightbox) return;
        openLightbox(Number(slide.getAttribute('data-media-index')));
      });
    });

    desktop.addEventListener('change', function () {
      var active = track.querySelector('.' + ACTIVE_SLIDE);
      if (active) scrollToSlide(active, false);
    });

    var initial = track.querySelector('.' + ACTIVE_SLIDE);
    if (initial) scrollToSlide(initial, false);

    return function showMedia(mediaId) {
      var slide = slideFor(mediaId);
      if (!slide) return;
      setActive(mediaId);
      scrollToSlide(slide, true);
    };
  }

  function initStickyGallery(section) {
    var col = section.querySelector('.oddit-section-main-product__gallery-col');
    if (!col) return;
    col.style.setProperty('--oddit-gallery-sticky-top', '20px');
  }

  function initLightbox(section) {
    var dialog = section.querySelector('[data-lightbox]');
    var openBtn = section.querySelector('[data-lightbox-open]');
    if (!dialog || !openBtn || !dialog.showModal) return null;
    var track = dialog.querySelector('[data-lightbox-track]');

    function open(index) {
      dialog.showModal();
      track.scrollLeft = index * track.clientWidth;
    }

    function step(dir) {
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }

    openBtn.addEventListener('click', function () {
      var active = section.querySelector('.' + ACTIVE_THUMB);
      open(active ? Number(active.getAttribute('data-media-index')) : 0);
    });

    dialog.querySelector('[data-lightbox-close]').addEventListener('click', function () {
      dialog.close();
    });
    // Click on the backdrop (outside the image) closes
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog || e.target.classList.contains('oddit-section-main-product__lightbox-slide')) dialog.close();
    });

    var prev = dialog.querySelector('[data-lightbox-prev]');
    var next = dialog.querySelector('[data-lightbox-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    });

    return open;
  }

  function initReviewsLink(section) {
    section.querySelectorAll('[data-reviews-link]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        var target =
          (href.length > 1 && document.getElementById(href.slice(1))) ||
          document.querySelector('[data-oddit-reviews-anchor]') ||
          document.querySelector('.okeReviews[data-oke-container]');
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function initSizeGuide(section) {
    var dialog = section.querySelector('[data-size-guide]');
    if (!dialog || !dialog.showModal) return;

    section.querySelectorAll('[data-size-guide-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        dialog.showModal();
      });
    });

    var close = dialog.querySelector('[data-size-guide-close]');
    if (close) close.addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
  }

  function initUgcVideos(section) {
    var modal = section.querySelector('[data-video-modal]');
    if (!modal || !modal.showModal) return;
    var body = modal.querySelector('[data-video-modal-body]');

    var hoverPreview = window.matchMedia('(hover: hover) and (pointer: fine)');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function startPreview(tile) {
      if (tile.querySelector('.oddit-section-main-product__video-preview')) return;
      var source = tile.querySelector('template[data-ugc-video-source]');
      var player = source && source.content.querySelector('video');
      if (!player) return;
      var preview = document.createElement('video');
      preview.className = 'oddit-section-main-product__video-preview';
      preview.muted = true;
      preview.defaultMuted = true;
      preview.loop = true;
      preview.playsInline = true;
      preview.preload = 'auto';
      preview.setAttribute('muted', '');
      preview.setAttribute('playsinline', '');
      preview.setAttribute('aria-hidden', 'true');
      preview.setAttribute('tabindex', '-1');
      player.querySelectorAll('source').forEach(function (src) {
        preview.appendChild(src.cloneNode(true));
      });
      if (!preview.childElementCount && player.getAttribute('src')) preview.src = player.getAttribute('src');
      tile.appendChild(preview);
      tile.classList.add('is-previewing');
      var playing = preview.play();
      if (playing && playing.catch) playing.catch(function () {});
    }

    function stopPreview(tile) {
      var preview = tile.querySelector('.oddit-section-main-product__video-preview');
      if (!preview) return;
      preview.pause();
      preview.removeAttribute('src');
      preview.remove();
      tile.classList.remove('is-previewing');
    }

    section.querySelectorAll('[data-ugc-video]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        stopPreview(tile);
        var source = tile.querySelector('template[data-ugc-video-source]');
        if (!source) return;
        body.replaceChildren(source.content.cloneNode(true));
        modal.showModal();
        var player = body.querySelector('video');
        if (player) player.play().catch(function () {});
      });

      tile.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !hoverPreview.matches || reduceMotion.matches) return;
        startPreview(tile);
      });
      tile.addEventListener('pointerleave', function () {
        stopPreview(tile);
      });
      tile.addEventListener('focus', function () {
        if (hoverPreview.matches && !reduceMotion.matches) startPreview(tile);
      });
      tile.addEventListener('blur', function () {
        stopPreview(tile);
      });
    });

    modal.querySelector('[data-video-modal-close]').addEventListener('click', function () {
      modal.close();
    });
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target === body) modal.close();
    });
    // Emptying the body stops playback and frees the video
    modal.addEventListener('close', function () {
      body.replaceChildren();
    });
  }

  /* ------------------------------------------------------------ Pill groups */

  // Shared by the main picker and bundle rows: native radio change -> active styling
  function initPillGroup(group) {
    group.addEventListener('change', function (e) {
      var input = e.target;
      if (input.type !== 'radio') return;
      group.querySelectorAll('[data-option-pill]').forEach(function (pill) {
        pill.classList.toggle(pill.getAttribute('data-active-class'), pill.contains(input));
      });
    });
  }

  /* --------------------------------------------------------------- Cart add */

  function addToCart(button, items) {
    if (!items.length) return;
    var wasDisabled = button.disabled;
    button.style.minWidth = button.getBoundingClientRect().width + 'px';
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    var drawer = window.OdditCartDrawer;
    var request = drawer
      ? (items.length === 1 ? drawer.addToCart(items[0].id, items[0].quantity) : drawer.addMultipleToCart(items)).then(function () {
          drawer.open();
        })
      : // No drawer on the page: add via the AJAX API and go to the cart page
        fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (res) {
          if (!res.ok) throw new Error('cart/add.js ' + res.status);
          window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart';
        });

    return request
      .catch(function (err) {
        console.error('[oddit-main-product]', err);
      })
      .finally(function () {
        button.disabled = wasDisabled;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        button.style.minWidth = '';
      });
  }

  /* ------------------------------------------------------ Sticky add to cart */

  var stickyObserver = null;

  function initStickyAtc(section) {
    var sticky = section.querySelector('[data-sticky-atc]');
    var anchor = sticky && section.querySelector(sticky.getAttribute('data-sticky-atc-anchor') || '[data-buy-buttons]');
    // Theme editor re-render: drop the observer still watching the old section
    if (stickyObserver) stickyObserver.disconnect();
    stickyObserver = null;
    if (!anchor || !('IntersectionObserver' in window)) return;

    stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Only once scrolled past (above the viewport), not while still below it
        var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        // BEM modifier, not .is-visible: styles.css.liquid has a global `.is-visible { display: block !important }`
        sticky.classList.toggle('oddit-section-main-product__sticky-atc--visible', passed);
        sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
        sticky.inert = !passed;
      });
    }, { threshold: 0 });
    stickyObserver.observe(anchor);
  }

  /* --------------------------------------------------------- Variant picker */

  function initVariants(section, showMedia) {
    var button = section.querySelector('[data-buy-buttons] [data-add-to-cart]');
    var picker = section.querySelector('[data-variant-picker]');
    var json = picker && picker.querySelector('[data-product-variants]');

    // One delegated handler for the in-page and sticky [data-add-to-cart] buttons
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart][data-variant-id]');
      if (btn) addToCart(btn, [{ id: Number(btn.getAttribute('data-variant-id')), quantity: Number(btn.getAttribute('data-quantity')) || 1 }]);
    });
    if (!picker || !json) return;

    var variants = JSON.parse(json.textContent);
    var format = section.getAttribute('data-money-format') || '${{amount}}';
    var initialId = button ? Number(button.getAttribute('data-variant-id')) : 0;
    var current = variants.find(function (v) { return v.id === initialId; }) || variants[0];
    var selected = current.options.slice();
    var priceEl = section.querySelector('[data-price]');
    var compareEl = section.querySelector('[data-price-compare]');
    var stickyBtn = section.querySelector('[data-sticky-atc-button]');
    var stickyVariant = section.querySelector('[data-sticky-atc-variant]');

    function match(options) {
      return variants.find(function (v) {
        return v.options.every(function (value, i) { return value === options[i]; });
      });
    }

    // Strike through values that are sold out or don't exist with the other current selections
    function markAvailability() {
      picker.querySelectorAll('[data-option-group]').forEach(function (group) {
        var index = Number(group.getAttribute('data-option-position')) - 1;
        group.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var options = selected.slice();
          options[index] = input.value;
          var variant = match(options);
          input.closest('[data-option-pill]').classList.toggle(
            'oddit-section-main-product__size-option--unavailable',
            !variant || !variant.available
          );
        });
      });
    }

    function setButton(btn, variant, label) {
      var available = !!(variant && variant.available);
      btn.disabled = !available;
      if (variant) btn.setAttribute('data-variant-id', variant.id);
      var text = !variant
        ? btn.getAttribute('data-unavailable-label') || 'Unavailable'
        : available
          ? label + ' • ' + formatMoney(variant.price, format)
          : btn.getAttribute('data-sold-out-label') || 'Sold out';
      var labelEl = btn.querySelector('[data-button-label]');
      if (labelEl) labelEl.textContent = text;
    }

    function render(variant) {
      if (button) setButton(button, variant, button.getAttribute('data-label'));
      if (stickyBtn) setButton(stickyBtn, variant, stickyBtn.getAttribute('data-label'));
      if (!variant) return;

      if (priceEl) priceEl.textContent = formatMoney(variant.price, format);
      if (compareEl) {
        var onSale = variant.compare_at_price > variant.price;
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price, format);
      }
      if (variant.featured_media) showMedia(variant.featured_media.id);

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());

      if (stickyVariant) stickyVariant.textContent = variant.title;
    }

    picker.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.hasAttribute('data-option-position')) return;
      var position = input.getAttribute('data-option-position');
      selected[Number(position) - 1] = input.value;

      var label = picker.querySelector('.oddit-section-main-product__picker-label-value[data-option-position="' + position + '"]');
      if (label) label.textContent = input.value;

      markAvailability();
      render(match(selected));
    });

    markAvailability();
  }

  /* ----------------------------------------------------------------- Bundle */

  function initBundle(section) {
    var card = section.querySelector('[data-bundle-card]');
    if (!card) return;
    var format = card.getAttribute('data-money-format') || '${{amount}}';
    var labels = card.querySelectorAll('[data-bundle-total-label], [data-bundle-add-to-cart] [data-button-label]');
    var button = card.querySelector('[data-bundle-add-to-cart]');

    function rows() {
      return Array.prototype.map.call(card.querySelectorAll('[data-bundle-item]'), function (item) {
        var picked = item.querySelector('.oddit-section-main-product__bundle-variant-input:checked');
        return {
          item: item,
          checked: item.querySelector('[data-bundle-check-input]').checked,
          id: Number(picked ? picked.value : item.getAttribute('data-default-variant-id')),
          cents: Number(picked ? picked.getAttribute('data-price-cents') : item.getAttribute('data-price-cents'))
        };
      });
    }

    function update() {
      var count = 0;
      var total = 0;
      rows().forEach(function (row) {
        var priceEl = row.item.querySelector('[data-bundle-price]');
        if (priceEl) priceEl.textContent = formatMoney(row.cents, format);
        if (!row.checked) return;
        count += 1;
        total += row.cents;
      });
      labels.forEach(function (el) {
        var template = el.getAttribute('data-template');
        if (template) el.textContent = template.replace('[count]', count).replace('[total]', formatMoney(total, format));
      });
      if (button) button.disabled = count === 0;
    }

    card.addEventListener('change', update);

    if (button) {
      button.addEventListener('click', function () {
        var items = rows()
          .filter(function (row) { return row.checked && row.id; })
          .map(function (row) { return { id: row.id, quantity: 1 }; });
        addToCart(button, items);
      });
    }
  }

  /* ------------------------------------------------------------------- Init */

  function init() {
    var section = document.querySelector('.oddit-section-main-product');
    if (!section || section.hasAttribute('data-initialized')) return;
    section.setAttribute('data-initialized', '');

    var showMedia = initGallery(section, initLightbox(section));
    initStickyGallery(section);
    initReviewsLink(section);
    initSizeGuide(section);
    initUgcVideos(section);

    section
      .querySelectorAll('.oddit-section-main-product__size-options, .oddit-section-main-product__bundle-variants')
      .forEach(initPillGroup);
    initVariants(section, showMedia);
    initStickyAtc(section);
    initBundle(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme editor re-renders the section without a page load
  document.addEventListener('shopify:section:load', init);
})();
